import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
    Button,
    Space,
    Typography,
    Spin,
    Alert,
    Descriptions,
    Divider,
    Radio,
    message,
    Card,
    Select,
} from 'antd';
import {
    CreditCardOutlined,
    GiftOutlined,
    CheckCircleOutlined,
    LockOutlined,
    PlusOutlined,
} from '@ant-design/icons';
import { loadStripe, StripeElementsOptions } from '@stripe/stripe-js';
import {
    Elements,
    PaymentElement,
    useStripe,
    useElements,
} from '@stripe/react-stripe-js';
import { useIntl } from 'react-intl';
import { useAuth } from '../../hooks/useAuth';
import {
    useCreateAddonPaymentIntentMutation,
    useGetPaymentMethodsQuery,
    useConfirmAddonPaymentMutation,
} from '../../services/nestjs-base-api';

const { Title, Text } = Typography;

const stripePromise =
    process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY &&
    process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY !==
        'pk_test_your_stripe_publishable_key_here'
        ? loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY)
        : null;

interface SavedPaymentMethod {
    id: string;
    brand: string;
    last4: string;
    expMonth: number;
    expYear: number;
}

interface AddonPreview {
    addon: {
        id: string;
        code: string;
        name: string;
        description: string;
        priceCents: number;
        currency: string;
    };
    availableCredits: Array<{
        redemptionId: string;
        type: string;
        availableCents: number;
        description: string;
    }>;
    totalAvailableCredit: number;
    finalPriceCents: number;
    discountApplied: number;
}

interface AddonCheckoutFormProps {
    organisationId: number;
    preview: AddonPreview;
    onSuccess: () => void;
    onCancel: () => void;
}

const formatPrice = (cents: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
    }).format(cents / 100);
};

// Get card brand display name and icon color
const getCardBrandInfo = (brand: string) => {
    const brandMap: Record<string, { name: string; color: string }> = {
        visa: { name: 'Visa', color: '#1A1F71' },
        mastercard: { name: 'Mastercard', color: '#EB001B' },
        amex: { name: 'American Express', color: '#006FCF' },
        discover: { name: 'Discover', color: '#FF6000' },
        diners: { name: 'Diners Club', color: '#0079BE' },
        jcb: { name: 'JCB', color: '#0B4EA2' },
        unionpay: { name: 'UnionPay', color: '#E21836' },
    };
    return brandMap[brand.toLowerCase()] || { name: brand, color: '#666' };
};

// Get browser's country from locale
function getBrowserCountry(): string {
    try {
        const locale = navigator.language || (navigator as any).userLanguage;
        if (locale && locale.includes('-')) {
            return locale.split('-')[1].toUpperCase();
        }
        // Common locale to country mappings
        const localeCountryMap: Record<string, string> = {
            'en': 'US',
            'de': 'DE',
            'fr': 'FR',
            'es': 'ES',
            'it': 'IT',
            'ja': 'JP',
            'zh': 'CN',
            'ko': 'KR',
            'pt': 'BR',
        };
        const baseLang = locale?.split('-')[0]?.toLowerCase();
        return localeCountryMap[baseLang] || 'US';
    } catch {
        return 'US';
    }
}

// Payment form component for NEW cards that uses Stripe hooks
const NewCardPaymentForm: React.FC<{
    organisationId: number;
    addonId: string;
    addonName: string;
    paymentIntentId: string;
    jwtToken: string;
    onSuccess: () => void;
    onCancel: () => void;
    isZeroAmount: boolean;
    billingCountry: string;
}> = ({ organisationId, addonId, addonName, paymentIntentId, jwtToken, onSuccess, onCancel, isZeroAmount, billingCountry }) => {
    const intl = useIntl();
    const stripe = useStripe();
    const elements = useElements();
    const [processing, setProcessing] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [confirmAddonPayment] = useConfirmAddonPaymentMutation();

    const handleSubmit = async () => {
        if (!stripe || !elements) {
            return;
        }

        setProcessing(true);
        setErrorMessage(null);

        try {
            // If amount is zero, skip Stripe payment confirmation but still confirm with backend
            if (isZeroAmount) {
                const confirmResult = await confirmAddonPayment({
                    jwtToken,
                    orgId: organisationId,
                    paymentIntentId,
                }).unwrap();

                if (confirmResult.success) {
                    message.success(intl.formatMessage({ id: 'addon.checkout.success', defaultMessage: 'Successfully purchased {addonName}!' }, { addonName }));
                    onSuccess();
                } else {
                    setErrorMessage(confirmResult.message || intl.formatMessage({ id: 'addon.checkout.activationFailed', defaultMessage: 'Failed to activate addon.' }));
                }
                return;
            }

            const { error, paymentIntent } = await stripe.confirmPayment({
                elements,
                confirmParams: {
                    return_url: `${window.location.origin}/dashboard/organisation?addon_purchase=success&addon=${addonId}`,
                    payment_method_data: {
                        billing_details: {
                            address: {
                                country: billingCountry,
                            },
                        },
                    },
                },
                redirect: 'if_required',
            });

            if (error) {
                setErrorMessage(error.message || intl.formatMessage({ id: 'addon.checkout.paymentFailed', defaultMessage: 'Payment failed. Please try again.' }));
            } else if (paymentIntent?.status === 'succeeded') {
                // Payment succeeded - confirm with backend to activate addon
                const confirmResult = await confirmAddonPayment({
                    jwtToken,
                    orgId: organisationId,
                    paymentIntentId,
                }).unwrap();

                if (confirmResult.success) {
                    message.success(intl.formatMessage({ id: 'addon.checkout.success', defaultMessage: 'Successfully purchased {addonName}!' }, { addonName }));
                    onSuccess();
                } else {
                    // Payment succeeded but addon activation failed - still show success
                    // The webhook should handle it, or user can refresh
                    message.warning(intl.formatMessage({ id: 'addon.checkout.activatingShortly', defaultMessage: 'Payment successful! Your addon will be activated shortly.' }));
                    onSuccess();
                }
            } else {
                // Payment requires additional action or is processing
                message.info(intl.formatMessage({ id: 'addon.checkout.processing', defaultMessage: 'Payment is being processed...' }));
                onSuccess();
            }
        } catch (err: any) {
            setErrorMessage(err.message || intl.formatMessage({ id: 'addon.checkout.unexpectedError', defaultMessage: 'An unexpected error occurred.' }));
        } finally {
            setProcessing(false);
        }
    };

    return (
        <div>
            {!isZeroAmount && (
                <div style={{ marginBottom: 16 }}>
                    <PaymentElement
                        options={{
                            layout: 'tabs',
                            fields: {
                                billingDetails: {
                                    address: {
                                        country: 'never', // We collect country separately for tax calculation
                                    },
                                },
                            },
                        }}
                    />
                </div>
            )}

            {errorMessage && (
                <Alert
                    message={errorMessage}
                    type="error"
                    showIcon
                    style={{ marginBottom: 16 }}
                />
            )}

            <div style={{ textAlign: 'right' }}>
                <Space>
                    <Button onClick={onCancel} disabled={processing}>
                        {intl.formatMessage({ id: 'common.cancel', defaultMessage: 'Cancel' })}
                    </Button>
                    <Button
                        type="primary"
                        onClick={handleSubmit}
                        loading={processing}
                        disabled={!stripe || !elements}
                        icon={isZeroAmount ? <CheckCircleOutlined /> : <LockOutlined />}
                    >
                        {isZeroAmount
                            ? intl.formatMessage({ id: 'addon.checkout.confirmPurchase', defaultMessage: 'Confirm Purchase' })
                            : intl.formatMessage({ id: 'addon.checkout.payNow', defaultMessage: 'Pay Now' })}
                    </Button>
                </Space>
            </div>

            <div style={{ marginTop: 16, textAlign: 'center' }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                    <LockOutlined style={{ marginRight: 4 }} />
                    {intl.formatMessage({ id: 'addon.checkout.stripeSecure', defaultMessage: 'Payments are securely processed by Stripe' })}
                </Text>
            </div>
        </div>
    );
};

// Payment form component for SAVED cards
const SavedCardPaymentForm: React.FC<{
    organisationId: number;
    addonName: string;
    paymentIntentId: string;
    clientSecret: string;
    savedPaymentMethodId: string;
    savedPaymentMethod: SavedPaymentMethod;
    jwtToken: string;
    onSuccess: () => void;
    onCancel: () => void;
}> = ({ organisationId, addonName, paymentIntentId, clientSecret, savedPaymentMethodId, savedPaymentMethod, jwtToken, onSuccess, onCancel }) => {
    const intl = useIntl();
    const [processing, setProcessing] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [confirmAddonPayment] = useConfirmAddonPaymentMutation();

    const handleSubmit = async () => {
        setProcessing(true);
        setErrorMessage(null);

        try {
            // Load stripe directly for saved card payment
            const stripe = await stripePromise;
            if (!stripe) {
                setErrorMessage(intl.formatMessage({ id: 'addon.checkout.stripeNotInitialized', defaultMessage: 'Stripe not initialized' }));
                return;
            }

            // Confirm payment with saved payment method
            const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
                payment_method: savedPaymentMethodId,
            });

            if (error) {
                setErrorMessage(error.message || intl.formatMessage({ id: 'addon.checkout.paymentFailed', defaultMessage: 'Payment failed. Please try again.' }));
            } else if (paymentIntent?.status === 'succeeded') {
                // Payment succeeded - confirm with backend to activate addon
                const confirmResult = await confirmAddonPayment({
                    jwtToken,
                    orgId: organisationId,
                    paymentIntentId,
                }).unwrap();

                if (confirmResult.success) {
                    message.success(intl.formatMessage({ id: 'addon.checkout.success', defaultMessage: 'Successfully purchased {addonName}!' }, { addonName }));
                    onSuccess();
                } else {
                    message.warning(intl.formatMessage({ id: 'addon.checkout.activatingShortly', defaultMessage: 'Payment successful! Your addon will be activated shortly.' }));
                    onSuccess();
                }
            } else if (paymentIntent?.status === 'requires_action') {
                // Handle 3D Secure or other authentication
                const { error: confirmError } = await stripe.confirmCardPayment(clientSecret);
                if (confirmError) {
                    setErrorMessage(confirmError.message || intl.formatMessage({ id: 'addon.checkout.authFailed', defaultMessage: 'Authentication failed.' }));
                } else {
                    const confirmResult = await confirmAddonPayment({
                        jwtToken,
                        orgId: organisationId,
                        paymentIntentId,
                    }).unwrap();

                    if (confirmResult.success) {
                        message.success(intl.formatMessage({ id: 'addon.checkout.success', defaultMessage: 'Successfully purchased {addonName}!' }, { addonName }));
                        onSuccess();
                    } else {
                        message.warning(intl.formatMessage({ id: 'addon.checkout.activatingShortly', defaultMessage: 'Payment successful! Your addon will be activated shortly.' }));
                        onSuccess();
                    }
                }
            } else {
                message.info(intl.formatMessage({ id: 'addon.checkout.processing', defaultMessage: 'Payment is being processed...' }));
                onSuccess();
            }
        } catch (err: any) {
            setErrorMessage(err.message || intl.formatMessage({ id: 'addon.checkout.unexpectedError', defaultMessage: 'An unexpected error occurred.' }));
        } finally {
            setProcessing(false);
        }
    };

    const brandInfo = getCardBrandInfo(savedPaymentMethod.brand);

    return (
        <div>
            <Card size="small" style={{ marginBottom: 16, background: '#fafafa' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <CreditCardOutlined style={{ fontSize: 24, marginRight: 12, color: brandInfo.color }} />
                        <div>
                            <Text strong>{intl.formatMessage({ id: 'addon.checkout.cardEndingIn', defaultMessage: '{brand} ending in {last4}' }, { brand: brandInfo.name, last4: savedPaymentMethod.last4 })}</Text>
                            <br />
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                {intl.formatMessage({ id: 'addon.checkout.expires', defaultMessage: 'Expires {date}' }, { date: `${savedPaymentMethod.expMonth.toString().padStart(2, '0')}/${savedPaymentMethod.expYear.toString().slice(-2)}` })}
                            </Text>
                        </div>
                    </div>
                    <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 20 }} />
                </div>
            </Card>

            {errorMessage && (
                <Alert
                    message={errorMessage}
                    type="error"
                    showIcon
                    style={{ marginBottom: 16 }}
                />
            )}

            <div style={{ textAlign: 'right' }}>
                <Space>
                    <Button onClick={onCancel} disabled={processing}>
                        {intl.formatMessage({ id: 'common.cancel', defaultMessage: 'Cancel' })}
                    </Button>
                    <Button
                        type="primary"
                        onClick={handleSubmit}
                        loading={processing}
                        icon={<LockOutlined />}
                    >
                        {intl.formatMessage({ id: 'addon.checkout.payNow', defaultMessage: 'Pay Now' })}
                    </Button>
                </Space>
            </div>

            <div style={{ marginTop: 16, textAlign: 'center' }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                    <LockOutlined style={{ marginRight: 4 }} />
                    {intl.formatMessage({ id: 'addon.checkout.stripeSecure', defaultMessage: 'Payments are securely processed by Stripe' })}
                </Text>
            </div>
        </div>
    );
};

const AddonCheckoutForm: React.FC<AddonCheckoutFormProps> = ({
    organisationId,
    preview,
    onSuccess,
    onCancel,
}) => {
    const intl = useIntl();
    const { user } = useAuth();
    const [clientSecret, setClientSecret] = useState<string | null>(null);
    const [paymentIntentId, setPaymentIntentId] = useState<string | null>(null);
    const [initError, setInitError] = useState<string | null>(null);
    const [applyCredits] = useState(true);
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>('new');
    const [hasInitializedPaymentMethod, setHasInitializedPaymentMethod] = useState(false);
    const [billingCountry, setBillingCountry] = useState<string>(getBrowserCountry());
    // Prevent duplicate payment intent creation (React 18 StrictMode)
    const paymentIntentCreatedRef = useRef(false);

    const [createPaymentIntent, { isLoading: creatingIntent }] =
        useCreateAddonPaymentIntentMutation();

    // Fetch saved payment methods (using billing endpoint for consistency)
    const { data: paymentMethodsData, isLoading: loadingPaymentMethods } = useGetPaymentMethodsQuery({
        jwtToken: user?.jwtToken || '',
        organisationId,
    });

    const isStripeConfigured = stripePromise !== null;
    const isZeroAmount = preview.finalPriceCents === 0;

    // Map payment methods to the SavedPaymentMethod interface
    const savedCards: SavedPaymentMethod[] = useMemo(
        () => (paymentMethodsData || []).map(pm => ({
            id: pm.stripePaymentMethodId,
            brand: pm.cardBrand || '',
            last4: pm.cardLast4 || '',
            expMonth: pm.cardExpMonth || 0,
            expYear: pm.cardExpYear || 0,
        })),
        [paymentMethodsData]
    );

    // Set default payment method to first saved card if available (only on initial load)
    useEffect(() => {
        if (savedCards.length > 0 && !hasInitializedPaymentMethod) {
            setSelectedPaymentMethod(savedCards[0].id);
            setHasInitializedPaymentMethod(true);
        }
    }, [savedCards, hasInitializedPaymentMethod]);

    // Create payment intent when component mounts or billing country changes
    useEffect(() => {
        const initPaymentIntent = async () => {
            // Prevent duplicate calls (React 18 StrictMode double-renders)
            if (!user?.jwtToken || !preview.addon.id || paymentIntentCreatedRef.current) return;

            paymentIntentCreatedRef.current = true;

            // If amount is zero (fully covered by credits), still create intent for tracking
            try {
                const result = await createPaymentIntent({
                    jwtToken: user.jwtToken,
                    orgId: organisationId,
                    addonId: preview.addon.id,
                    applyCredits,
                    billingCountry, // Pass billing country for tax calculation
                }).unwrap();

                setClientSecret(result.clientSecret);
                setPaymentIntentId(result.paymentIntentId);
            } catch (error: any) {
                setInitError(
                    error?.data?.message || intl.formatMessage({ id: 'addon.checkout.initFailed', defaultMessage: 'Failed to initialize checkout. Please try again.' })
                );
                paymentIntentCreatedRef.current = false; // Allow retry on error
            }
        };

        initPaymentIntent();
    }, [user?.jwtToken, organisationId, preview.addon.id, applyCredits, billingCountry, createPaymentIntent, intl]);

    if (!isStripeConfigured) {
        return (
            <Alert
                message={intl.formatMessage({ id: 'addon.checkout.stripeNotConfigured', defaultMessage: 'Stripe Not Configured' })}
                description={intl.formatMessage({ id: 'addon.checkout.stripeNotConfiguredDesc', defaultMessage: 'Payment processing is not available because Stripe is not properly configured. Please contact your administrator.' })}
                type="warning"
                showIcon
            />
        );
    }

    if (initError) {
        return (
            <Alert
                message={intl.formatMessage({ id: 'addon.checkout.checkoutError', defaultMessage: 'Checkout Error' })}
                description={initError}
                type="error"
                showIcon
            />
        );
    }

    const elementsOptions: StripeElementsOptions = {
        clientSecret: clientSecret || undefined,
        appearance: {
            theme: 'stripe',
            variables: {
                colorPrimary: '#F59E0B',
            },
        },
    };

    const selectedSavedCard = savedCards.find(card => card.id === selectedPaymentMethod);

    return (
        <div>
            {/* Order Summary */}
            <Descriptions column={1} bordered size="small" style={{ marginBottom: 16 }}>
                <Descriptions.Item label={intl.formatMessage({ id: 'addon.checkout.addon', defaultMessage: 'Add-on' })}>
                    <Text strong>{preview.addon.name}</Text>
                </Descriptions.Item>
                {preview.addon.description && (
                    <Descriptions.Item label={intl.formatMessage({ id: 'addon.checkout.description', defaultMessage: 'Description' })}>
                        {preview.addon.description}
                    </Descriptions.Item>
                )}
                <Descriptions.Item label={intl.formatMessage({ id: 'addon.checkout.price', defaultMessage: 'Price' })}>
                    {formatPrice(preview.addon.priceCents, preview.addon.currency)} <Text type="secondary">{(preview.addon.currency || 'USD').toUpperCase()}</Text>
                </Descriptions.Item>
                {preview.discountApplied > 0 && (
                    <Descriptions.Item label={intl.formatMessage({ id: 'addon.checkout.rewardCreditApplied', defaultMessage: 'Reward Credit Applied' })}>
                        <Text type="success">
                            -{formatPrice(preview.discountApplied)} <Text type="secondary">{(preview.addon.currency || 'USD').toUpperCase()}</Text>
                        </Text>
                    </Descriptions.Item>
                )}
                <Descriptions.Item label={intl.formatMessage({ id: 'addon.checkout.total', defaultMessage: 'Total' })}>
                    <Title level={4} style={{ margin: 0 }}>
                        {formatPrice(preview.finalPriceCents, preview.addon.currency)} <Text type="secondary" style={{ fontSize: 14, fontWeight: 'normal' }}>{(preview.addon.currency || 'USD').toUpperCase()}</Text>
                    </Title>
                </Descriptions.Item>
            </Descriptions>
            <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 4, marginBottom: 8 }}>
                {intl.formatMessage({ id: 'addon.checkout.plusTax', defaultMessage: 'Plus applicable taxes' })}
            </Text>

            {/* Credit info alerts */}
            {preview.availableCredits.length > 0 && preview.discountApplied > 0 && (
                <Alert
                    message={
                        <span>
                            <GiftOutlined style={{ marginRight: 8 }} />
                            {intl.formatMessage({ id: 'addon.checkout.creditAppliedMessage', defaultMessage: '{amount} reward credit applied to this purchase.' }, { amount: formatPrice(preview.discountApplied) })}
                        </span>
                    }
                    type="info"
                    style={{ marginBottom: 16 }}
                />
            )}

            {isZeroAmount && (
                <Alert
                    message={intl.formatMessage({ id: 'addon.checkout.fullyCovoredTitle', defaultMessage: 'Your reward credits fully cover this add-on!' })}
                    description={intl.formatMessage({ id: 'addon.checkout.fullyCoveredDesc', defaultMessage: 'No payment required - just click confirm to complete your purchase.' })}
                    type="success"
                    icon={<CheckCircleOutlined />}
                    showIcon
                    style={{ marginBottom: 16 }}
                />
            )}

            <Divider />

            {/* Payment Method Selection */}
            {creatingIntent || loadingPaymentMethods ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                    <Spin size="large" />
                    <div style={{ marginTop: 16 }}>
                        <Text type="secondary">{intl.formatMessage({ id: 'addon.checkout.preparingCheckout', defaultMessage: 'Preparing checkout...' })}</Text>
                    </div>
                </div>
            ) : clientSecret && paymentIntentId ? (
                <>
                    {/* Payment Method Selection - only show if not zero amount and has saved cards */}
                    {!isZeroAmount && savedCards.length > 0 && (
                        <div style={{ marginBottom: 16 }}>
                            <Text strong style={{ display: 'block', marginBottom: 8 }}>
                                <CreditCardOutlined style={{ marginRight: 8 }} />
                                {intl.formatMessage({ id: 'addon.checkout.paymentMethod', defaultMessage: 'Payment Method' })}
                            </Text>
                            <Radio.Group
                                value={selectedPaymentMethod}
                                onChange={(e) => setSelectedPaymentMethod(e.target.value)}
                                style={{ width: '100%' }}
                            >
                                <Space direction="vertical" style={{ width: '100%' }}>
                                    {savedCards.map((card) => {
                                        const brandInfo = getCardBrandInfo(card.brand);
                                        return (
                                            <Radio
                                                key={card.id}
                                                value={card.id}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    padding: '12px',
                                                    border: '1px solid #d9d9d9',
                                                    borderRadius: '6px',
                                                    width: '100%',
                                                    backgroundColor: selectedPaymentMethod === card.id ? '#f6ffed' : '#fff',
                                                    borderColor: selectedPaymentMethod === card.id ? '#52c41a' : '#d9d9d9',
                                                }}
                                            >
                                                <div style={{ display: 'flex', alignItems: 'center', marginLeft: 8 }}>
                                                    <CreditCardOutlined style={{ fontSize: 20, marginRight: 8, color: brandInfo.color }} />
                                                    <span>
                                                        <Text strong>{intl.formatMessage({ id: 'addon.checkout.cardEndingIn', defaultMessage: '{brand} ending in {last4}' }, { brand: brandInfo.name, last4: card.last4 })}</Text>
                                                        <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
                                                            {intl.formatMessage({ id: 'addon.checkout.expires', defaultMessage: 'Expires {date}' }, { date: `${card.expMonth.toString().padStart(2, '0')}/${card.expYear.toString().slice(-2)}` })}
                                                        </Text>
                                                    </span>
                                                </div>
                                            </Radio>
                                        );
                                    })}
                                    <Radio
                                        value="new"
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            padding: '12px',
                                            border: '1px solid #d9d9d9',
                                            borderRadius: '6px',
                                            width: '100%',
                                            backgroundColor: selectedPaymentMethod === 'new' ? '#e6f7ff' : '#fff',
                                            borderColor: selectedPaymentMethod === 'new' ? '#1890ff' : '#d9d9d9',
                                        }}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', marginLeft: 8 }}>
                                            <PlusOutlined style={{ fontSize: 20, marginRight: 8, color: '#1890ff' }} />
                                            <Text strong>{intl.formatMessage({ id: 'addon.checkout.addNewCard', defaultMessage: 'Add new card' })}</Text>
                                        </div>
                                    </Radio>
                                </Space>
                            </Radio.Group>
                            <Divider />
                        </div>
                    )}

                    {/* Billing Country Selection - only show for new cards */}
                    {!isZeroAmount && (selectedPaymentMethod === 'new' || savedCards.length === 0) && (
                        <div style={{ marginBottom: 16 }}>
                            <Text strong style={{ display: 'block', marginBottom: 8 }}>
                                {intl.formatMessage({ id: 'addon.checkout.billingCountry', defaultMessage: 'Billing Country' })}
                            </Text>
                            <Select
                                value={billingCountry}
                                onChange={(value) => {
                                    setBillingCountry(value);
                                    // Reset payment intent when billing country changes (tax may change)
                                    if (clientSecret) {
                                        setClientSecret(null);
                                        setPaymentIntentId(null);
                                        paymentIntentCreatedRef.current = false;
                                    }
                                }}
                                style={{ width: '100%' }}
                                showSearch
                                optionFilterProp="label"
                                options={[
                                    { value: 'AU', label: 'Australia' },
                                    { value: 'US', label: 'United States' },
                                    { value: 'GB', label: 'United Kingdom' },
                                    { value: 'CA', label: 'Canada' },
                                    { value: 'NZ', label: 'New Zealand' },
                                    { value: 'DE', label: 'Germany' },
                                    { value: 'FR', label: 'France' },
                                    { value: 'JP', label: 'Japan' },
                                    { value: 'SG', label: 'Singapore' },
                                    { value: 'IN', label: 'India' },
                                    { value: 'BR', label: 'Brazil' },
                                    { value: 'MX', label: 'Mexico' },
                                    { value: 'ES', label: 'Spain' },
                                    { value: 'IT', label: 'Italy' },
                                    { value: 'NL', label: 'Netherlands' },
                                    { value: 'SE', label: 'Sweden' },
                                    { value: 'CH', label: 'Switzerland' },
                                    { value: 'AT', label: 'Austria' },
                                    { value: 'BE', label: 'Belgium' },
                                    { value: 'DK', label: 'Denmark' },
                                    { value: 'FI', label: 'Finland' },
                                    { value: 'IE', label: 'Ireland' },
                                    { value: 'NO', label: 'Norway' },
                                    { value: 'PL', label: 'Poland' },
                                    { value: 'PT', label: 'Portugal' },
                                    { value: 'CZ', label: 'Czech Republic' },
                                    { value: 'HU', label: 'Hungary' },
                                    { value: 'RO', label: 'Romania' },
                                    { value: 'GR', label: 'Greece' },
                                    { value: 'ZA', label: 'South Africa' },
                                    { value: 'AE', label: 'United Arab Emirates' },
                                    { value: 'HK', label: 'Hong Kong' },
                                    { value: 'TW', label: 'Taiwan' },
                                    { value: 'KR', label: 'South Korea' },
                                    { value: 'TH', label: 'Thailand' },
                                    { value: 'MY', label: 'Malaysia' },
                                    { value: 'ID', label: 'Indonesia' },
                                    { value: 'PH', label: 'Philippines' },
                                    { value: 'VN', label: 'Vietnam' },
                                    { value: 'AR', label: 'Argentina' },
                                    { value: 'CL', label: 'Chile' },
                                    { value: 'CO', label: 'Colombia' },
                                    { value: 'PE', label: 'Peru' },
                                    { value: 'IL', label: 'Israel' },
                                    { value: 'SA', label: 'Saudi Arabia' },
                                    { value: 'EG', label: 'Egypt' },
                                    { value: 'NG', label: 'Nigeria' },
                                    { value: 'KE', label: 'Kenya' },
                                ].sort((a, b) => a.label.localeCompare(b.label))}
                            />
                        </div>
                    )}

                    {/* Payment Form - show based on selection */}
                    {selectedPaymentMethod === 'new' || savedCards.length === 0 ? (
                        <Elements stripe={stripePromise} options={elementsOptions}>
                            <NewCardPaymentForm
                                organisationId={organisationId}
                                addonId={preview.addon.id}
                                addonName={preview.addon.name}
                                paymentIntentId={paymentIntentId}
                                jwtToken={user?.jwtToken || ''}
                                onSuccess={onSuccess}
                                onCancel={onCancel}
                                isZeroAmount={isZeroAmount}
                                billingCountry={billingCountry}
                            />
                        </Elements>
                    ) : selectedSavedCard ? (
                        <SavedCardPaymentForm
                            organisationId={organisationId}
                            addonName={preview.addon.name}
                            paymentIntentId={paymentIntentId}
                            clientSecret={clientSecret}
                            savedPaymentMethodId={selectedPaymentMethod}
                            savedPaymentMethod={selectedSavedCard}
                            jwtToken={user?.jwtToken || ''}
                            onSuccess={onSuccess}
                            onCancel={onCancel}
                        />
                    ) : null}
                </>
            ) : (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                    <Spin />
                </div>
            )}
        </div>
    );
};

export default AddonCheckoutForm;
